import { useAuth } from "@/contexts/AuthContext";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { useDeployContext } from "@/contexts/DeployContext";
import { useFlow } from "@/contexts/FlowContext";
import { useCurrentMedia, useCurrentWorkspace } from "@/store";
import { Flows, DeploySteps, BuildSteps } from "@/types/flows";
import { useCallback } from "react";
import { useModel } from "@/contexts/ModelContext";
import { useFlowState } from "@/hooks/useFlowState";
import { useAsyncBoundOperation } from "@/hooks/useApi";
import { ErrorInfo } from "@/components/ErrorInfo";
import Button from "@scripts/react/components/Button";
import { TooltipContainer } from "@scripts/react/components/Tooltip";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { usePageNavigationShortcut } from "@/hooks/usePageNavigationShortcut";

interface PublishActionButtonProps {
    disabled?: boolean;
}

export const PublishActionButton = ({ disabled = false }: PublishActionButtonProps) => {
    // Use legacy hook for data access and optimized hooks for other features
    const { task } = useFlow();
    const { setStep } = useFlowState();
    const currentMedia = useCurrentMedia();
    const { createRapidWorkflowDeploy, creatingRapidWorkflow } = useDeployContext();
    const { isDownloading } = useModel();
    const {
        currentPromptingData,
        currentInferenceSampledData,
        hasEnoughDataToDeploy,
        imagesNeededToDeploy
    } = useComputedValues();
    const { user } = useAuth();
    const currentWorkspace = useCurrentWorkspace();

    const handleCreateRapidWorkflowDeploy = useCallback(async () => {
        if (disabled) return;

        if (!user) {
            console.warn("PublishActionButton: No user, redirecting to flow: build, step: preview");
            setStep({ flow: Flows.BUILD, step: BuildSteps.PREVIEW });
            return;
        }

        try {
            const workflow = await createRapidWorkflowDeploy({
                task,
                workspaceId: currentWorkspace?.id,
                prompts: currentPromptingData,
                inferenceData: currentInferenceSampledData
            });
            setStep({ flow: Flows.DEPLOY, step: DeploySteps.DEPLOY, workflowUrl: workflow.url });
            return workflow;
        } catch (err) {
            console.error("PublishActionButton createRapidWorkflow error", err);
            throw new Error(`There was an error creating your workflow. Please try again. ${err}`);
        }
    }, [
        createRapidWorkflowDeploy,
        currentInferenceSampledData,
        currentPromptingData,
        currentWorkspace?.id,
        disabled,
        setStep,
        task,
        user
    ]);

    const {
        isLoading: isLoadingCreateRapidWorkflow,
        error: errorCreateRapidWorkflow,
        invoke: invokeCreateRapidWorkflow
    } = useAsyncBoundOperation(handleCreateRapidWorkflowDeploy);

    const handleCreateRapidWorkflowDeployClick = useCallback(() => {
        // TODO: add tracking?
        invokeCreateRapidWorkflow();
    }, [invokeCreateRapidWorkflow]);

    const handleCreateRapidWorkflowDeployRetry = useCallback(() => {
        // TODO: add tracking?
        invokeCreateRapidWorkflow();
    }, [invokeCreateRapidWorkflow]);

    const isLoading = isLoadingCreateRapidWorkflow || creatingRapidWorkflow;
    const isButtonDisabled = disabled || isLoading || !hasEnoughDataToDeploy;

    const tooltipTitle = !hasEnoughDataToDeploy
        ? `Upload ${imagesNeededToDeploy} more image${imagesNeededToDeploy !== 1 ? "s" : ""} to publish your model`
        : undefined;

    // Keyboard shortcut: Command + Right Arrow
    usePageNavigationShortcut("right", handleCreateRapidWorkflowDeployClick, isButtonDisabled);

    const buttonContent = (
        <Button
            type={!isDownloading ? "primary" : "secondary"}
            disabled={isButtonDisabled}
            loading={isLoading}
            onClick={handleCreateRapidWorkflowDeployClick}
            className="w-full"
        >
            <i className="far fa-rocket"></i>
            <span>Publish and Use Model</span>
            <div
                className={twClassNames(
                    "hotkeysContainer",
                    (isButtonDisabled || isLoading) && "opacity-30"
                )}
            >
                <span className="hotkey dark-brand">⌘</span>
                <span className="hotkey dark-brand">→</span>
            </div>
        </Button>
    );

    return (
        <>
            {errorCreateRapidWorkflow && (
                <ErrorInfo
                    error={errorCreateRapidWorkflow}
                    retrying={isLoadingCreateRapidWorkflow}
                    onRetryClick={handleCreateRapidWorkflowDeployRetry}
                />
            )}
            {isButtonDisabled && !isLoading && !currentMedia?.file.isVideo ? (
                <TooltipContainer
                    type="titleOnly"
                    title={tooltipTitle}
                    customButton={<div className="w-full">{buttonContent}</div>}
                />
            ) : (
                buttonContent
            )}
        </>
    );
};
